import { EMPLOYEE_PRESENCE } from "@benefit-systems/hr-tools-ui";
import { DateRange } from "@mui/x-date-pickers-pro";
import { ResponseTypeList } from "src/Pages/Company/Absences/api/api";
import { IAbsenceGroup } from "src/Pages/Company/Absences/AbsenceGroups/AbsenceGroups.models";
import {
  EPatternUnit,
  PresenceType,
  IAbsenceTypeConfigurationOptions,
  IAbsenceTypeConfigurationOptionsBase,
  ICustomFieldDTO,
  BillingCycleConfigurationType,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { IExcludedDay } from "src/Pages/Company/Absences/Calendars/Calendars.models";
import { EmploymentForm } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/EmployeeEmployment/constants/employmentForms";

export enum Status {
  ACCEPTED = "ACCEPTED",
  CANCELLED = "CANCELLED",
  WAITING_FOR_APPROVAL = "WAITING_FOR_APPROVAL",
  CORRECTION_REQUIRED = "CORRECTION_REQUIRED",
  REJECTED = "REJECTED",
}

export const StatusOptions = [
  { value: Status.ACCEPTED, label: "Zaakceptowany" },
  {
    value: Status.WAITING_FOR_APPROVAL,
    label: "Oczekuje na akceptację",
  },
  { value: Status.CORRECTION_REQUIRED, label: "Wymagana zmiana" },
  { value: Status.CANCELLED, label: "Anulowany" },
  { value: Status.REJECTED, label: "Odrzucony" },
];

export enum ApproverType {
  IMMEDIATE_SUPERIOR = "IMMEDIATE_SUPERIOR",
  INTERMEDIATE_SUPERIOR = "INTERMEDIATE_SUPERIOR",
  AHR = "AHR",
}

export enum HistoryAction {
  CREATION = "CREATION",
  EDITION = "EDITION",
  CANCELLATION = "CANCELLATION",
  CANCELLATION_AFTER_CALENDAR_CHANGE = "CANCELLATION_AFTER_CALENDAR_CHANGE",
  CANCELLATION_AFTER_PATTERN_ASSIGNMENT_CHANGE = "CANCELLATION_AFTER_PATTERN_ASSIGNMENT_CHANGE",
  CANCELLATION_AFTER_ABSENCE_TYPE_SUBMIT_LIMITATION_CHANGE = "CANCELLATION_AFTER_ABSENCE_TYPE_SUBMIT_LIMITATION_CHANGE",
  ACCEPTANCE = "ACCEPTANCE",
  REJECTION = "REJECTION",
  REQUIRE_CORRECTION = "REQUIRE_CORRECTION",
}

export enum RequesterType {
  AHR = "AHR",
  OMB = "OMB",
  EMPLOYEE = "EMPLOYEE",
  UNKNOWN = "UNKNOWN",
  SYSTEM = "SYSTEM",
}

export enum AbsenceDayPosition {
  SINGLE = "single",
  START = "start",
  MIDDLE = "middle",
  END = "end",
}

export interface IAbsenceTypeInfoRequest {
  id: string;
  name: string;
  description?: string;
  presenceType: PresenceType;
  configurationOptions: Pick<
    IAbsenceTypeConfigurationOptions,
    | "billInHours"
    | "billInCalendarDays"
    | "multipleRequestsPerDayAllowed"
    | "modificationAllowed"
  >;
}

export interface IAbsence {
  employeeId: string;
  date: string;
  absenceTypeId: string;
  absenceName: string;
  absenceRequestId: string;
  status: Status;
  absenceGroupId: string;
  isEffective: boolean;
  presenceType: PresenceType;
}

export interface IEmployeeAbsences {
  absences: IAbsence[];
  absenceGroupInfos: IAbsenceGroup[];
  absenceTypeInfos: IAbsenceTypeInfoRequest[];
  absencePlans: string[];
  calendarInfos: {
    availableYears: string[];
    details: {
      [key: string]: {
        absencePlanningEnabled: boolean;
        excludedDays: IExcludedDay[];
      };
    };
  };
}

export interface IAbsenceRequestDay {
  date: string;
  absenceName: string;
  bgColor: string;
  color: string;
  absenceRequestId: string;
  multipleRequestsPerDayAllowed: boolean;
  position: AbsenceDayPosition;
  title: string;
}

// TODO: cleanup types - after stabilizing API merge Employee interface into one
export interface IEmployeeBase {
  employeeId: string;
  firstName: string;
  lastName: string;
}

export interface IEmployeeListItem extends IEmployeeBase {
  type: ApproverType;
}

export interface IEmployeeDetails extends IEmployeeBase {
  active: boolean;
  additionalDisplayArea: string | null;
  area: string | null;
  avatarUrl?: string;
  contractFrom: string | null;
  contractTo: string | null;
  dailyWorkingHours: number;
  email: string | null;
  employeeId: string;
  employmentType: EmploymentForm | null;
  firstName: string;
  fkNumber: string;
  initials: string;
  lastName: string;
  localization: string | null;
  parentArea: string | null;
  phone: string | null;
  position: string | null;
  presenceStatus: EMPLOYEE_PRESENCE;
  presentAtQueriedTimeRange?: boolean;
  workingHours: {
    nominator: number | null;
    denominator: number | null;
  };
}

export interface IEmployeesAutocomplete {
  name: string;
  label: string;
  enabled: boolean;
  absenceTypeId?: string;
  queryKey: string;
  queryFn: ({
    params,
  }: Record<string, unknown>) => Promise<ResponseTypeList<IEmployeeDetails>>;
  additionalQueryParams?: { [key: string]: string };
  required?: boolean;
  hasDefaultValue?: boolean;
  setFirstAsDefault?: boolean;
  disabled?: boolean;
  showInactiveLabel?: boolean;
}

export interface IAbsenceRequestFormValues {
  absenceType: string;
  unit: EPatternUnit;
  dates: DateRange<Date>;
  date: Date | null;
  startTime: Date | null;
  endTime: Date | null;
  approver: IEmployeeDetails;
  replacer: IEmployeeDetails | null;
  comment: string;
  justification: string;
  attachments: { id: string }[];
}

export interface AbsenceTypeCycleDay {
  type: EPatternUnit;
  dates: string[];
}

export interface AbsenceTypeCycleHour {
  type: EPatternUnit;
  date: string;
  startTime: string;
  endTime: string;
}

export interface IAbsenceRequestDTO {
  absenceTypeId: string;
  employeeId: string;
  absences: AbsenceTypeCycleDay | AbsenceTypeCycleHour;
  approverId: string;
  replacerId?: string;
  justification: string;
  comment: string;
}

export interface IRequireCorrectionDTO {
  note: string;
}

export interface IAbsenceTimes {
  date: string;
  endTime?: string;
  startTime?: string;
}

export interface IAbsenceRequestRange {
  start: {
    date: string;
    time: string | null;
  };
  end: {
    date: string;
    time: string | null;
  };
}

export interface ITimeAmount {
  days: number;
  hours: number;
}

export type CustomFieldValues = Record<string, string | string[] | boolean>;

export interface ICustomFieldsConfiguration {
  customFields: ICustomFieldDTO[];
}

interface IAbsenceRequestBase {
  absenceRequestId: string;
  actionsAllowed: {
    editable: boolean;
    approvable: boolean;
    rejectable: boolean;
    cancelable: boolean;
    sendForCorrectionEligible: boolean;
  };
  type: EPatternUnit;
  absenceTimes: IAbsenceTimes[];
  effectiveAbsenceTimes: IAbsenceTimes[];
  effectiveTimeAmount: ITimeAmount;
  totalTimeAmount: ITimeAmount;
  status: Status;
}

export interface IApproverDetails extends IEmployeeDetails {
  isActive: boolean;
}

export interface IReplacerDetails extends IEmployeeDetails {
  activeAtRequestTimeRange: boolean;
  presentAtRequestTimeRange: boolean;
}

export interface IAbsenceRequest extends IAbsenceRequestBase {
  employee: {
    employeeId: string;
  };
  absenceType: {
    absenceTypeId: string;
    name: string;
    description?: string;
    presenceType: PresenceType;
    billingCycleType: BillingCycleConfigurationType | undefined;
    configurationOptions: IAbsenceTypeConfigurationOptionsBase & {
      submitLimitationFrom: string;
      submitLimitationTo: string;
    };
    customFieldsConfiguration: ICustomFieldsConfiguration | null;
    approvers: ApproverType[];
  };
  approver: IApproverDetails;
  replacer: IReplacerDetails;
  justification: string;
  attachments?: {
    attachmentId: string;
    fileName: string;
  }[];
  note: {
    creationDate: string;
    creator: IEmployeeBase;
    content: string;
  };
  comment: string;
  customFieldValues: CustomFieldValues;
  eTag: string;
}

export interface IAbsenceRequestListItem extends IAbsenceRequestBase {
  employee: IEmployeeDetails;
  absenceType: {
    absenceTypeId: string;
    name: string;
    description?: string;
    presenceType: PresenceType;
    absenceGroup: {
      color: string;
    };
    configurationOptions: Pick<
      IAbsenceTypeConfigurationOptions,
      "modificationAllowed" | "multipleRequestsPerDayAllowed"
    >;
  };
  range: IAbsenceRequestRange;
  creationTime: string;
  acceptationTime: string;
  rejectionTime: string;
  cancellationTime: string;
  approvedBy: IEmployeeBase;
  approver: IEmployeeBase;
  eTag: string;
}

export interface IAbsenceRequestRequireCorrectionFormValues {
  note: string;
}

interface IAbsenceRequestHistoryChange<T> {
  before: T;
  after: T;
}

export enum AbsenceRequestHistoryEntryDetailsTypes {
  TYPE = "type",
  RANGE = "range",
  TIME_AMOUNT = "timeAmount",
  APPROVER = "approver",
  REPLACER = "replacer",
  ATTACHMENTS = "attachments",
  JUSTIFICATION = "justification",
  COMMENT = "comment",
  NOTE = "note",
}

export const HistoryEntryDetailsLabels = {
  [AbsenceRequestHistoryEntryDetailsTypes.TYPE]: "Tryb wniosku",
  [AbsenceRequestHistoryEntryDetailsTypes.RANGE]: "Termin wniosku",
  [AbsenceRequestHistoryEntryDetailsTypes.TIME_AMOUNT]:
    "Liczba dni/godzin wniosku",
  [AbsenceRequestHistoryEntryDetailsTypes.APPROVER]: "Osoba akceptująca",
  [AbsenceRequestHistoryEntryDetailsTypes.REPLACER]: "Osoba zastępująca",
  [AbsenceRequestHistoryEntryDetailsTypes.ATTACHMENTS]: "Załączniki",
  [AbsenceRequestHistoryEntryDetailsTypes.JUSTIFICATION]: "Uzasadnienie",
  [AbsenceRequestHistoryEntryDetailsTypes.COMMENT]: "Komentarz",
  [AbsenceRequestHistoryEntryDetailsTypes.NOTE]: "Notatka",
};

export interface IAbsenceRequestHistoryEntryDetails {
  [AbsenceRequestHistoryEntryDetailsTypes.TYPE]: IAbsenceRequestHistoryChange<EPatternUnit> | null;
  [AbsenceRequestHistoryEntryDetailsTypes.RANGE]: IAbsenceRequestHistoryChange<IAbsenceRequestRange> | null;
  [AbsenceRequestHistoryEntryDetailsTypes.TIME_AMOUNT]: IAbsenceRequestHistoryChange<ITimeAmount> | null;
  [AbsenceRequestHistoryEntryDetailsTypes.APPROVER]: IAbsenceRequestHistoryChange<IEmployeeDetails> | null;
  [AbsenceRequestHistoryEntryDetailsTypes.REPLACER]: IAbsenceRequestHistoryChange<IEmployeeDetails> | null;
  [AbsenceRequestHistoryEntryDetailsTypes.JUSTIFICATION]: IAbsenceRequestHistoryChange<string> | null;
  [AbsenceRequestHistoryEntryDetailsTypes.ATTACHMENTS]: IAbsenceRequestHistoryChange<
    string[]
  > | null;
}

export type AbsenceRequestHistoryTableValue =
  | EPatternUnit
  | IAbsenceRequestRange
  | ITimeAmount
  | IEmployeeDetails
  | string
  | string[]
  | null;
export interface IAbsenceRequestHistoryTableEntry {
  id: AbsenceRequestHistoryEntryDetailsTypes;
  before: AbsenceRequestHistoryTableValue;
  after: AbsenceRequestHistoryTableValue;
}

export interface IAbsenceRequestHistoryEntry {
  action: HistoryAction;
  status: Status;
  requester: {
    type: RequesterType;
    firstName: string;
    lastName: string;
    metadata: object;
  };
  actionTime: string;
  details: IAbsenceRequestHistoryEntryDetails | null;
  order: number;
}
export interface IAbsenceRequestHistory {
  entries: IAbsenceRequestHistoryEntry[];
}

interface IAbsencePlanEntry {
  from: string;
  to: string;
  absenceTypeId: string | null;
}
export interface IAbsencePlan {
  employeeId: string;
  year: number;
  entries: IAbsencePlanEntry[];
}

export interface IAbsenceRequestsWithETags {
  absenceRequestId: string;
  eTag: string;
}

export interface IAbsenceRequestFormAbsenceType {
  id: string;
  name: string;
  description?: string;
  presenceType: PresenceType;
  billingCycleType: BillingCycleConfigurationType | null | undefined;
  approvers: ApproverType[];
  configurationOptions: IAbsenceTypeConfigurationOptionsBase;
  customFieldsConfiguration: ICustomFieldsConfiguration | null;
  submitLimitationFrom: string; // ISO date in format YYYY-MM-DD
  submitLimitationTo: string; // ISO date in format YYYY-MM-DD
}
